import { useEffect, useState, useRef, useCallback } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Loader2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import { tokenResolutionApi } from '@/services/api/tokenResolution'

interface SessionInfo {
  session_id: string
  section_pages: number[]
  section_identifier: string | null
}

interface PdfPageResponse {
  pdf_base64: string
  page_number: number
  total_pages: number
}

interface PdfTeacherPreviewModalProps {
  isOpen: boolean
  teacherCode: string | null
  sessions: SessionInfo[]
  initialSessionIndex: number
  onClose: () => void
}

export function PdfTeacherPreviewModal({ 
  isOpen, 
  teacherCode, 
  sessions,
  initialSessionIndex,
  onClose 
}: PdfTeacherPreviewModalProps) {
  const [pdfBase64, setPdfBase64] = useState<string | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentSessionIndex, setCurrentSessionIndex] = useState(initialSessionIndex)
  const [zoom, setZoom] = useState(100)
  const loadedSessionRef = useRef<string | null>(null)

  // Reset session index and zoom when modal opens with new teacher
  useEffect(() => {
    setCurrentSessionIndex(initialSessionIndex)
    setZoom(100)
    loadedSessionRef.current = null
  }, [initialSessionIndex, teacherCode])

  const loadPage = useCallback(async (sessionId: string, pageNum: number) => {
    setLoading(true)
    setError(null)
    try {
      const response: PdfPageResponse = await tokenResolutionApi.getSessionPdfPage(sessionId, pageNum)
      setPdfBase64(response.pdf_base64)
      setPageNumber(response.page_number)
      setTotalPages(response.total_pages)
      loadedSessionRef.current = `${sessionId}-${pageNum}`
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden des PDFs')
      setPdfBase64(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load PDF when session index changes or modal opens
  useEffect(() => {
    if (!isOpen || sessions.length === 0 || currentSessionIndex >= sessions.length) return
    
    const session = sessions[currentSessionIndex]
    if (!session) return
    
    const sessionKey = `${session.session_id}-${currentSessionIndex}`
    
    // Only load if this session hasn't been loaded yet
    if (loadedSessionRef.current !== sessionKey) {
      // Pass the page number explicitly from section_pages to ensure correct page is shown
      const rawPageNum = session.section_pages?.[0]
      const pageNum = typeof rawPageNum === 'number' ? rawPageNum : parseInt(String(rawPageNum), 10) || 1
      console.log('[PdfTeacherPreviewModal] Loading PDF:', {
        sessionId: session.session_id,
        sectionPages: session.section_pages,
        rawPageNum,
        pageNum,
        sessionKey
      })
      loadPage(session.session_id, pageNum)
    }
  }, [isOpen, currentSessionIndex, sessions, loadPage])

  const handlePrevSession = useCallback(() => {
    loadedSessionRef.current = null
    setCurrentSessionIndex(prev => Math.max(0, prev - 1))
  }, [])

  const handleNextSession = useCallback(() => {
    loadedSessionRef.current = null
    setCurrentSessionIndex(prev => Math.min(sessions.length - 1, prev + 1))
  }, [sessions.length])

  const handlePrevPage = useCallback(() => {
    if (pageNumber > 1) {
      const session = sessions[currentSessionIndex]
      if (session) {
        loadPage(session.session_id, pageNumber - 1)
      }
    }
  }, [pageNumber, sessions, currentSessionIndex, loadPage])

  const handleNextPage = useCallback(() => {
    if (pageNumber < totalPages) {
      const session = sessions[currentSessionIndex]
      if (session) {
        loadPage(session.session_id, pageNumber + 1)
      }
    }
  }, [pageNumber, totalPages, sessions, currentSessionIndex, loadPage])

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 25, 300))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 25, 25))
  }, [])

  const handleZoomReset = useCallback(() => {
    setZoom(100)
  }, [])

  const hasMultipleSessions = sessions.length > 1

  return (
    <Modal 
      isOpen={isOpen} 
      title={`PDF Vorschau - ${teacherCode || 'Lehrer'}${hasMultipleSessions ? ` (${currentSessionIndex + 1}/${sessions.length})` : ''}`} 
      size="full"
      onClose={onClose}
    >
      <div className="h-[75vh] flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-500">
            {error}
          </div>
        ) : pdfBase64 ? (
          <>
            {/* Toolbar with zoom and navigation */}
            <div className="flex flex-wrap justify-center gap-2 p-3 border-b bg-gray-50">
              {/* Session navigation */}
              {hasMultipleSessions && (
                <div className="flex gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handlePrevSession}
                    disabled={currentSessionIndex <= 0}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Session
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleNextSession}
                    disabled={currentSessionIndex >= sessions.length - 1}
                  >
                    Session
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
              
              {/* Page navigation */}
              <div className="flex gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={pageNumber <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm text-gray-700 flex items-center">
                  Seite {pageNumber} von {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={pageNumber >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Zoom controls */}
              <div className="flex gap-1 ml-2 border-l pl-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={zoom <= 25}
                  title="Verkleinern"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="px-2 py-1 text-sm text-gray-700 flex items-center min-w-[60px] justify-center">
                  {zoom}%
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={zoom >= 300}
                  title="Vergrössern"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleZoomReset}
                  title="Originalgrösse"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* PDF display area with zoom */}
            <div className="flex-grow overflow-auto bg-gray-100">
              <div 
                className="flex items-start justify-center min-h-full p-4"
                style={{
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease-out'
                }}
              >
                <iframe
                  key={`${sessions[currentSessionIndex]?.session_id}-${pageNumber}-${zoom}`}
                  src={`data:application/pdf;base64,${pdfBase64}`}
                  className="bg-white shadow-lg"
                  style={{
                    width: '100%',
                    minWidth: '800px',
                    height: '100vh',
                    border: 'none'
                  }}
                  title="PDF Preview"
                />
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Kein PDF verfügbar
          </div>
        )}
      </div>
    </Modal>
  )
}
